import React, { useState, useEffect } from "react";
import BitrixAPI from "../api/b24";
import { FilesContainer } from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import PrintButton from '../components/PrintButton';
import FileUpload from "../components/FileUpload";
import { useHeader } from "../contexts/HeaderContext";

const ScladOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [finishedOrders, setFinishedOrders] = useState([]);
  const [argonDoneOrders, setArgonDoneOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [purchasingOrders, setPurchasingOrders] = useState(new Set());
  const [argoningOrders, setArgoningOrders] = useState(new Set());
  const [activeTab, setActiveTab] = useState("activeOrders");
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileFilter, setFileFilter] = useState("all"); // all, uploaded, not-uploaded
  const { setHeaderExtras } = useHeader();

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}

  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = async () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        const succeeded = await onConfirm(orderId);
        if (!succeeded) {
          setIsCompleted(false);
        }
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : 'bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      
      const result = await BitrixAPI.updateStagesclad(orderId);
  
      if (result.success) {
        if (activeTab === "activeOrders") {
          setOrders(prevOrders => 
            prevOrders.filter(order => order.id !== orderId)
          );
        } else if (activeTab === "completedOrders") {
          setCompletedOrders(prevOrders => 
            prevOrders.filter(order => order.id !== orderId)
          );
        }
        console.log('Заказ успешно обновлен');
        return true;
      } else {
        throw new Error('Не удалось обновить стадию');
      }
  
    } catch (error) {
      console.error('Ошибка при обновлении стадии:', error);
      setError(`Ошибка при обновлении стадии: ${error.message}`);
      return false;
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handlePurchase = async (orderId) => {
    try {
      setPurchasingOrders(prev => new Set([...prev, orderId]));

      const result = await BitrixAPI.updateStageToPurchase(orderId);

      if (result.success) {
        setOrders(prevOrders =>
          prevOrders.filter(order => order.id !== orderId)
        );
        console.log('Заказ успешно переведен в закупку');
      } else {
        throw new Error('Не удалось перевести заказ в закупку');
      }

    } catch (error) {
      console.error('Ошибка при переводе заказа в закупку:', error);
      setError(`Ошибка при переводе в закупку: ${error.message}`);
    } finally {
      setPurchasingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleArgon = async (orderId) => {
    try {
      setArgoningOrders(prev => new Set([...prev, orderId]));
      const result = await BitrixAPI.updateStageToArgon(orderId);
      if (result.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        console.log('Заказ успешно переведен на аргонную сварку');
      } else {
        throw new Error('Не удалось перевести заказ на аргон');
      }
    } catch (error) {
      console.error('Ошибка при переводе заказа на аргон:', error);
      setError(`Ошибка при переводе на аргон: ${error.message}`);
    } finally {
      setArgoningOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleUploadSuccess = (message) => {
    setUploadSuccess(message);
    setUploadError(null);
    // Автоматически скрыть уведомление через 5 секунд
    setTimeout(() => setUploadSuccess(null), 5000);

    // Перезагружаем данные заказов для отображения нового файла
    fetchData(false);
  };

  const handleUploadError = (message) => {
    setUploadError(message);
    setUploadSuccess(null);
  };

  const normalizeOrders = (orders) => {
    if (!Array.isArray(orders)) {
      console.error('Полученные заказы не являются массивом:', orders);
      return [];
    }
    
    return orders.map(order => {
      const mainFiles = Array.isArray(order.files) 
        ? order.files.filter(file => file && file.id)
        : [];
        
      const prodFiles = Array.isArray(order.productionFiles) 
        ? order.productionFiles.filter(file => file && file.id)
        : [];

      // Файлы фурнитуры уже обработаны в API
      console.log(`Order ${order.id} storageFiles from API:`, order.storageFiles);
      console.log(`Order ${order.id} raw ufCrm3_1753795707997:`, order.ufCrm3_1753795707997);
      
      return {
        ...order,
        id: order.id,
        orderNumber: order.orderNumber || 'Без номера',
        title: order.title || 'Без названия',
        files: mainFiles,
        productionFiles: prodFiles,
        storageFiles: order.storageFiles || [] // Используем уже обработанные данные из API
      };
    });
  };

  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const activeOrdersResponse = await BitrixAPI.getActiveOrderssclad();
      console.log('Raw API response:', activeOrdersResponse);
      const normalizedOrders = normalizeOrders(activeOrdersResponse);
      console.log('Normalized orders:', normalizedOrders);

      // Определяем новые заказы только для activeOrders вкладки
      if (activeTab === "activeOrders" && !isInitialLoad) {
        const newOrders = normalizedOrders.filter(newOrder =>
          !orders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов:`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }
      }

      setOrders(normalizedOrders);

      if (activeTab === "completedOrders") {
        const completedOrdersResponse = await BitrixAPI.getActiveOrderszakup();
        setCompletedOrders(normalizeOrders(completedOrdersResponse));
      }

      if (activeTab === "inventory") {
        const finishedOrdersResponse = await BitrixAPI.getCompletedOrders();
        // Данные уже приходят отсортированными от новых к старым из API
        setFinishedOrders(normalizeOrders(finishedOrdersResponse));
      }

      if (activeTab === "argonDone") {
        const argonDoneResponse = await BitrixAPI.getArgonDoneOrders();
        setArgonDoneOrders(normalizeOrders(argonDoneResponse));
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{orders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, orders.length]);

  // Первоначальная загрузка заказов
  useEffect(() => {
    fetchData(true);
  }, [activeTab]);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive || activeTab !== "activeOrders") {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log('🔄 Проверка новых заказов...');
      fetchData(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, activeTab, orders]); // orders в зависимостях для сравнения с новыми

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  const renderOrdersList = (ordersList) => {
    if (!Array.isArray(ordersList) || ordersList.length === 0) {
      return (
        <div className="bg-white p-4 shadow rounded-lg">
          <p className="text-gray-600">Нет заказов для отображения</p>
        </div>
      );
    }

    // Фильтрация заказов по поисковому запросу и статусу файлов
    const filteredOrders = ordersList.filter(order => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        order.orderNumber.toLowerCase().includes(searchLower) ||
        order.title.toLowerCase().includes(searchLower) ||
        order.id.toString().includes(searchLower)
      );
      
      const hasFiles = order.storageFiles && order.storageFiles.length > 0;
      let matchesFileFilter = true;
      
      if (fileFilter === "uploaded") {
        matchesFileFilter = hasFiles;
      } else if (fileFilter === "not-uploaded") {
        matchesFileFilter = !hasFiles;
      }
      
      return matchesSearch && matchesFileFilter;
    });

    if (filteredOrders.length === 0 && (searchTerm || fileFilter !== "all")) {
      return (
        <div className="bg-white p-4 shadow rounded-lg">
          <p className="text-gray-600">Ничего не найдено по заданным фильтрам</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <div 
            key={order.id} 
            className="bg-white p-4 shadow rounded-lg transform transition-all duration-300 hover:shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-medium">Заказ №{order.orderNumber}</h2>
                <p className="text-gray-600">{order.title}</p>
                <p className="text-xs text-gray-500">Deal ID: {order.id}</p>
                {order.completedDate && (
                  <p className="text-xs text-gray-500">
                    Дата выполнения: {order.completedDate}
                  </p>
                )}
                {/* Индикатор загрузки файла */}
                <div className="flex items-center mt-2">
                  <span className="text-xs text-gray-500 mr-2">Файлы фурнитуры:</span>
                  {order.storageFiles && order.storageFiles.length > 0 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Загружено ({order.storageFiles.length})
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Не загружено
                    </span>
                  )}
                </div>
              </div>
              {activeTab === "activeOrders" && (
                <div className="flex space-x-2 flex-wrap gap-2">
                  <PrintButton
                    fileId={order.productionFiles && order.productionFiles.length > 0 ? order.productionFiles[0].id : null}
                    fileUrl={order.productionFiles && order.productionFiles.length > 0 ? order.productionFiles[0].url : null}
                    label="Печать"
                    color="blue"
                    overlayText={{
                      orderNumber: order.orderNumber,
                      topText: `Заказ №${order.orderNumber}`,
                      bottomLines: [
                        "Собрал заказ: ______________________________________________",
                        "Выдал заказ:  ______________________________________________",
                        "Принял заказ: ______________________________________________"
                      ]
                    }}
                  />
                  <LoadingButton
                    isLoading={purchasingOrders.has(order.id)}
                    onClick={() => handlePurchase(order.id)}
                    label="Закупка"
                    color="blue"
                  />
                  <LoadingButton
                    isLoading={argoningOrders.has(order.id)}
                    onClick={() => handleArgon(order.id)}
                    label="Аргон"
                    color="purple"
                  />
                </div>
              )}
            </div>

            {order.productionFiles && order.productionFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                <FilesContainer 
                  files={order.productionFiles}
                  dealId={order.id}
                />
              </div>
            )}

            {order.files && order.files.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                <FilesContainer 
                  files={order.files}
                  dealId={order.id}
                />
              </div>
            )}

            {/* Файлы фурнитуры */}
            {order.storageFiles && order.storageFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Файлы фурнитуры:</h3>
                <FilesContainer 
                  files={order.storageFiles}
                  dealId={order.id}
                />
              </div>
            )}

            {/* Компонент загрузки файлов фурнитуры */}
            <FileUpload 
              orderId={order.id}
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
              uploadEndpoint="upload-storage-file"
              fieldType="storage"
            />

            {/* Свайп-кнопка во всю ширину блока */}
            {(activeTab === "activeOrders" || activeTab === "completedOrders") && (
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={handleStageUpdate}
                  isLoading={processingOrders.has(order.id)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4">

      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-green-50 border-2 border-green-400 text-green-800 px-4 py-3 rounded-lg mb-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="text-lg">Поступило {newOrdersNotification.count} новых заказов!</strong>
                <div className="text-sm text-green-700 mt-1">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="ml-2 text-green-600 hover:text-green-800 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Поиск и фильтры */}
      <div className="mb-6 space-y-4">
        {/* Фильтр по статусу загрузки файлов */}
        <div className="flex space-x-2">
          <button
            className={`px-3 py-1 rounded text-sm ${
              fileFilter === "all" 
                ? "bg-blue-500 text-white" 
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={() => setFileFilter("all")}
          >
            Все
          </button>
          <button
            className={`px-3 py-1 rounded text-sm ${
              fileFilter === "uploaded" 
                ? "bg-green-500 text-white" 
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={() => setFileFilter("uploaded")}
          >
            С фурнитурой
          </button>
          <button
            className={`px-3 py-1 rounded text-sm ${
              fileFilter === "not-uploaded" 
                ? "bg-red-500 text-white" 
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={() => setFileFilter("not-uploaded")}
          >
            Без фурнитуры
          </button>
        </div>
      </div>
      
      {/* Вкладки */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "activeOrders"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("activeOrders")}
            >
              Активные заказы
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "completedOrders"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("completedOrders")}
            >
              Закуп
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "inventory"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("inventory")}
            >
              Готовые
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "argonDone"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("argonDone")}
            >
              Аргон Готово
            </button>
          </li>
        </ul>
      </div>
      
      
      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 p-4 rounded-lg mb-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Уведомления о загрузке файлов */}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          <strong>Успех:</strong> {uploadSuccess}
          <button 
            onClick={() => setUploadSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            ✕
          </button>
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка загрузки:</strong> {uploadError}
          <button 
            onClick={() => setUploadError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Содержимое вкладок */}
      {loading ? (
        <div className="p-4 bg-white shadow rounded-lg">
          <p className="text-gray-600">Загрузка данных...</p>
        </div>
      ) : (
        <>
          {activeTab === "activeOrders" && renderOrdersList(orders)}
          {activeTab === "completedOrders" && renderOrdersList(completedOrders)}
          {activeTab === "inventory" && renderOrdersList(finishedOrders)}
          {activeTab === "argonDone" && renderOrdersList(argonDoneOrders)}
        </>
      )}
    </div>
  );
};

export default ScladOrdersPageB24;
